import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { usePagination } from 'pagination-react-js'
import { useParams, useNavigate, Link, NavLink } from 'react-router-dom'
import Markdown from 'react-markdown'
import rehypeUnwrapImages from 'rehype-unwrap-images'
import { DATE_NOW, PUBLIC_URL, ROUTES } from '../../lib/constants'
import styles from './Blog.module.scss'
import { Image } from '../../components/Image/Image'
import imageM from '../../images/blog-page-hero-m.jpg'
import imageD from '../../images/blog-page-hero-d.jpg'
import { SVGIcon } from '../../components/SVGIcon/SVGIcon'
import { Button } from '../../components/Button/Button'

type ArticleParts = {
  image: string | null
  headline: string | null
  limitedContent: string | null
  articleIndex: number
}

export const Blog = (): React.JSX.Element => {
  const [index, setIndex] = useState(1)
  const [numErrors, setNumErrors] = useState(0)
  const [mdArray, setMdArray] = useState<{ content: string; fileIndex: number }[]>([])
  const [articlePartsArr, setArticlePartsArr] = useState<ArticleParts[]>()
  const [loaded, setLoaded] = useState(false)
  const { page } = useParams()
  const navigate = useNavigate()

  const maxArticlesPerPage = 5;
  const articlePageOffset = 4;

  const { records, pageNumbers, setActivePage } = usePagination({
    activePage: parseInt(page as string),
    recordsPerPage: maxArticlesPerPage,
    totalRecordsLength: mdArray.length,
    offset: 1,
    navCustomPageSteps: { prev: articlePageOffset, next: articlePageOffset },
    /* permanentFirstNumber: true,
    permanentLastNumber: true, */
  })

  const extractH1 = (markdown: string): string | null => {
    const match = markdown.match(/^# (.*)$/m)
    return match ? match[0] : null
  }

  const extractImage = (markdown: string): string | null => {
    const match = markdown.match(/!\[(.*?)\]\((.*?)\)/m)
    return match ? match[0] : null
  }

  const extractlimitedContent = (markdown: string): string | null => {
    const match = markdown.match(/^(?!\s*$).+/gm)
    return (
      match
        ?.filter((v) => !v.includes('rehype'))
        .find((v) => !v.startsWith('#') && !v.startsWith('!')) || null
    )
  }

  const loadMarkdown = useCallback(async () => {
    const resp = await fetch(
      `/blogs/article-${index}.md`
    )
    const result = await resp.text()
    if (result.startsWith('#')) {
      setMdArray([...mdArray, { content: result, fileIndex: index }])
      setIndex(index + 1)
    } else {
      setNumErrors(numErrors + 1)
      setIndex(index + 1)
    }
  }, [index, mdArray, numErrors])

  useEffect(() => {
    /* console.log(records, pageNumbers); */
    if (page && parseInt(page) > pageNumbers.lastPage) {
      navigate(ROUTES.blog.path.replace(':page', '1'), { replace: true })
    }
  }, [pageNumbers])

  useEffect(() => {
    if (numErrors < 3) {
      try {
        loadMarkdown()
      } catch (err) {
        setNumErrors(numErrors + 1)
        setIndex(index + 1)
      }
    } else {
      setLoaded(true)
    }
  }, [index, numErrors])

  useEffect(() => {
    if (page) {
      setActivePage(parseInt(page))
      if (loaded) {
        setTimeout(() => {
          document
            .querySelector('#articles img')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 40)
      }
    } else {
      navigate(ROUTES.blog.path.replace(':page', '1'), { replace: true })
    }
  }, [page])

  useEffect(() => {
    if (loaded) {
      const arr: ArticleParts[] = mdArray.map((md) => ({
        image: extractImage(md.content),
        headline: extractH1(md.content),
        limitedContent: extractlimitedContent(md.content),
        articleIndex: md.fileIndex,
      }))
      setArticlePartsArr(arr)
    }
  }, [loaded, mdArray])

  return (
    <>
      <Helmet>
        <title>{ROUTES.blog.title}</title>
        <meta
          name="description"
          content="Stay updated with the latest news from Cityside Fiber as we bring fast, reliable internet connections to more communities."
        />
      </Helmet>

      <section className={styles.hero}>
        <div className={styles.designElement}></div>

        <div className="grid-12">
          <div className={styles.headlineWrap}>
            <h1 className="headline__h1 color--white">Blog & News</h1>
          </div>
        </div>

        <div className={styles.imageWrap}>
          <Image
            sources={[
              { src: imageM, breakpoint: 'default' },
              { src: imageD, breakpoint: '960px' },
            ]}
            alt="man looking at laptop"
            loading="eager"
          />
        </div>
      </section>

      <section id="articles" className={styles.articles}>
        <div className="grid-12">
          <div className={styles.container}>
            {!!articlePartsArr &&
              articlePartsArr
                .slice(records.indexOfFirst, records.indexOfLast + 1)
                .map((parts, i) => (
                  <div
                    key={`article-${records.indexOfFirst + i}`}
                    className={styles.articleCard}
                  >
                    {parts.image ? (
                      <Markdown
                        rehypePlugins={[
                          [rehypeUnwrapImages, { fragment: true }],
                        ]}
                        components={{
                          img(props) {
                            // eslint-disable-next-line
                            const { node, children, src, alt, ...rest } = props
                            return (
                              <Image
                                src={`${PUBLIC_URL}blogs/${src}`}
                                alt={alt}
                                {...rest}
                              />
                            )
                          },
                        }}
                      >
                        {parts.image}
                      </Markdown>
                    ) : (
                      <Image />
                    )}

                    <div className={styles.content}>
                      {!!parts.headline && (
                        <Markdown
                          components={{
                            h1: 'h2',
                          }}
                        >
                          {parts.headline}
                        </Markdown>
                      )}

                      {!!parts.limitedContent && (
                        <Markdown>{parts.limitedContent}</Markdown>
                      )}

                      <Button
                        path={ROUTES.blogArticle.path.replace(
                          ':id',
                          parts.articleIndex.toString()
                        )}
                        size="md"
                        gradient="orange"
                      >
                        Read More{' '}
                        <SVGIcon id="arrow" color="white" rotate={180} />
                      </Button>
                    </div>
                  </div>
                ))}
          </div>

          <div className={styles.container} style={{ textAlign: 'center' }}>
            <nav className={styles.pageNav}>
              <Link
                to={
                  pageNumbers.previousPage
                    ? ROUTES.blog.path.replace(
                        ':page',
                        pageNumbers.previousPage.toString()
                      )
                    : ''
                }
                data-disabled={!pageNumbers.previousPage}
              >
                <SVGIcon id="chevron" color="midnight-blue" rotate={180} />
                Previous
              </Link>

              {!!pageNumbers.customPreviousPage && (
                <Link
                  to={ROUTES.blog.path.replace(
                    ':page',
                    pageNumbers.customPreviousPage.toString()
                  )}
                >
                  ...
                </Link>
              )}

              {pageNumbers.navigation.map((val) => (
                <NavLink
                  key={`page-${val}`}
                  to={ROUTES.blog.path.replace(':page', val.toString())}
                >
                  {val}
                </NavLink>
              ))}

              {!!pageNumbers.customNextPage && (
                <Link
                  to={ROUTES.blog.path.replace(
                    ':page',
                    pageNumbers.customNextPage.toString()
                  )}
                >
                  ...
                </Link>
              )}

              <Link
                to={
                  pageNumbers.nextPage
                    ? ROUTES.blog.path.replace(
                        ':page',
                        pageNumbers.nextPage.toString()
                      )
                    : ''
                }
                data-disabled={!pageNumbers.nextPage}
              >
                Next
                <SVGIcon id="chevron" color="midnight-blue" />
              </Link>
            </nav>
          </div>
        </div>
      </section>
    </>
  )
}
